//@flow
//Copy of app/jsx/components/common/breaks/SingleBreak.jsx with necessary changes
import * as React from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';
import Input from 'common/components/core/input';
import PlacesSearchBox from 'common/components/core/map/PlacesSearchBox';
import DateTimePicker from 'common/components/core/form/DateTimePicker';
import Select from 'common/components/core/select/Select';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { Button, ButtonTypes } from 'common/components/core/button';

import { type TFunction } from 'react-i18next';
import { type ThemeType } from 'react-css-themr';
import { type BreakShift } from 'common/models/internal/BreakShift';
import { type Homebase } from 'common/models/internal/Homebase';
import { type List } from 'immutable';

const daysList = Object.values(DayOfWeekType);
type DaysListType = $Values<typeof DayOfWeekType>;

type OnBreakValueChangeArgsType = {
    fieldId: string,
    offset?: number,
    value?: moment | number,
    breakIndex: number,
    breakType: string,
    description: string,
    lat?: number,
    lng?: number
};

type Props = {
    onBreakValueChange?: OnBreakValueChangeArgsType => void,
    breakType: string,
    breakIndex: number,
    deleteBreak?: (
        breakType: string,
        breakKey: string,
        breakIndex: number
    ) => void,
    breakKey: string,
    currentDate: ?string,
    handleFieldFocus: ?(breakKey: string) => void,
    breakSlotFor: DaysListType,
    endDayOffset: number,
    isActive: boolean,
    isUnapplied: boolean,
    homebases: ?List<Homebase>,
    theme: ThemeType,
    shiftStart: moment,
    shiftEnd: moment,
    breakShift: BreakShift,
    t: TFunction
};

export class SingleBreakWithOffset extends React.Component<Props> {
    onBreakValueChange = data => {
        const { onBreakValueChange, breakType, breakIndex } = this.props;
        onBreakValueChange?.({
            ...data,
            breakType,
            breakIndex
        });
    };

    deleteBreak = () => {
        const { deleteBreak, breakType, breakKey, breakIndex } = this.props;
        deleteBreak?.(breakType, breakKey, breakIndex);
    };

    onDurationChange = (value, fieldId, ...args) => {
        this.onBreakValueChange({ value, fieldId }, ...args);
    };

    onStartTimeChange = (value?: moment, offset: number) => {
        const { currentDate } = this.props;
        this.onBreakValueChange({
            value: value || moment(currentDate),
            fieldId: 'startTime',
            offset
        });
    };

    onEndTimeChange = (value?: moment, offset: number) => {
        const { currentDate } = this.props;
        this.onBreakValueChange({
            value: value || moment(currentDate),
            fieldId: 'endTime',
            offset
        });
    };

    onLocationChange = (lat, lng, description, ...args) => {
        this.onBreakValueChange(
            { lat, lng, description, fieldId: 'location' },
            ...args
        );
    };

    handleFocus = () => {
        const { handleFieldFocus, breakKey } = this.props;
        handleFieldFocus?.(breakKey);
    };

    getOffsetDayOptions = () => {
        const { breakSlotFor, endDayOffset, t } = this.props;
        const startIndex = daysList.indexOf(breakSlotFor);
        const endIndex = startIndex + endDayOffset + 1;
        let daysToShow = daysList.slice(startIndex, endIndex);
        if (endIndex > 6) {
            daysToShow = [...daysToShow, ...daysList.slice(0, endIndex % 7)];
        }
        return daysToShow.map(day => ({
            label: t(`entityManagement:shiftTable.days.${day}`),
            value: day
        }));
    };

    getSelectedDay = (day: number, breakSlotFor: DaysListType): DaysListType =>
        /*  day if the offset which ranges from 1 to 7
            breakSlotFor is seleted shift day ranges from Monday to Sunday
            This function computes the final day based on offset and shift day
            '% 7' ensures values is always between 0 and 6
        */
        daysList[(day - 1 + daysList.indexOf(breakSlotFor)) % 7];

    getStartValue = () => {
        const {
            breakShift: {
                startShiftInstant: { day }
            },
            breakSlotFor,
            t
        } = this.props;
        const selectedDay = this.getSelectedDay(day, breakSlotFor);
        return t(`entityManagement:shiftTable.days.${selectedDay}`);
    };

    getEndValue = () => {
        const {
            breakShift: {
                endShiftInstant: { day }
            },
            breakSlotFor,
            t
        } = this.props;
        const selectedDay = this.getSelectedDay(day, breakSlotFor);
        return t(`entityManagement:shiftTable.days.${selectedDay}`);
    };

    changeStartOffset = ({ value }: { value: DaysListType }) => {
        const {
            breakShift: { startShiftInstant },
            breakSlotFor
        } = this.props;
        const startTime = startShiftInstant.getDayAdjustedTimeMoment();
        const offset =
            (daysList.indexOf(value) - daysList.indexOf(breakSlotFor) + 7) % 7;
        this.onStartTimeChange(startTime, offset);
    };

    changeEndOffset = ({ value }: { value: DaysListType }) => {
        const {
            breakShift: { endShiftInstant },
            breakSlotFor
        } = this.props;
        const endTime = endShiftInstant.getDayAdjustedTimeMoment();
        const offset =
            (daysList.indexOf(value) - daysList.indexOf(breakSlotFor) + 7) % 7;
        this.onEndTimeChange(endTime, offset);
    };

    render() {
        const {
            isActive,
            isUnapplied,
            homebases,
            theme,
            t,
            shiftStart,
            shiftEnd,
            endDayOffset,
            breakShift
        } = this.props;
        const {
            startShiftInstant,
            endShiftInstant,
            duration: transactionDuration,
            location
        } = breakShift;
        const startTime = startShiftInstant.getDayAdjustedTimeMoment();
        const endTime = endShiftInstant.getDayAdjustedTimeMoment();
        const startLocationAddress = location?.getAddressString() || '';
        const shiftEndMoment =
            shiftEnd.diff(shiftStart, 'd') !== endDayOffset
                ? moment(shiftEnd).add(endDayOffset, 'd')
                : shiftEnd;
        const breakEndError =
            breakShift
                .setAllValidationErrors()
                .getValidationMessage('endShiftInstant') ||
            breakShift.validateBreakEndWithShift({
                shiftEnd: shiftEndMoment,
                endShiftInstant
            });
        const breakStartError = breakShift.validateBreakStartWithShift({
            shiftStart,
            startShiftInstant
        });
        const dayOptions = this.getOffsetDayOptions();
        return (
            <div
                className={classnames(theme.breakWrap, {
                    [theme.activeBreak]: isActive,
                    [theme.unappliedBreak]: isUnapplied
                })}
            >
                <Input
                    type="number"
                    fieldId="duration"
                    autoFocus
                    theme={theme}
                    min="0"
                    pattern="\d*"
                    value={transactionDuration / 60}
                    onChange={this.onDurationChange}
                    rightIcon={t('translations:minutes_short')}
                    onFocus={this.handleFocus}
                />
                <label>{t('breakBetween')}</label>
                <Select
                    options={dayOptions}
                    value={this.getStartValue()}
                    isClearable={false}
                    theme={theme}
                    onChange={this.changeStartOffset}
                />
                <DateTimePicker
                    showDate={false}
                    theme={theme}
                    fieldId="startTime"
                    value={startTime}
                    className={theme.startTime}
                    onChange={this.onStartTimeChange}
                    onFocus={this.handleFocus}
                    min={shiftStart}
                    error={breakStartError}
                />
                <label>{t('to')}</label>
                <Select
                    options={dayOptions}
                    value={this.getEndValue()}
                    isClearable={false}
                    theme={theme}
                    onChange={this.changeEndOffset}
                />
                <DateTimePicker
                    showDate={false}
                    fieldId="endTime"
                    className={theme.endTime}
                    value={endTime}
                    theme={theme}
                    onChange={this.onEndTimeChange}
                    onFocus={this.handleFocus}
                    min={shiftStart}
                    max={shiftEndMoment}
                    error={breakEndError}
                />
                <label>{t('at')}</label>
                <PlacesSearchBox
                    fieldId="startTime"
                    selectedAddress={startLocationAddress}
                    cachedSearchList={homebases}
                    theme={theme}
                    onPlacesChanged={this.onLocationChange}
                    placeholder={t('optionalAddress')}
                    disabled={false}
                    portal
                    useDefaultCenter
                />
                <Button
                    ripple={false}
                    buttonType={ButtonTypes.Icon}
                    icon="delete-bin"
                    onClick={this.deleteBreak}
                    theme={theme}
                    className={theme.deleteBtn}
                />
            </div>
        );
    }
}

export default SingleBreakWithOffset;
